import { useMemo, useState } from "react";
import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";

/** Uma opção da lista: o que a tela mostra e o id que o `PUT` manda. */
export interface TeamMultiSelectOption {
  id: string;
  label: string;
  /** Linha secundária — e-mail do funcionário, slug da loja. */
  hint?: string | null;
  /** Etiqueta à direita: papel, status. */
  badge?: string | null;
  /** Desativado no produto (funcionário inativo, loja suspensa): continua listado, marcado. */
  muted?: boolean;
}

interface TeamMultiSelectProps {
  options: TeamMultiSelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
  searchPlaceholder: string;
  emptyMessage: string;
}

/**
 * A lista de múltipla seleção dos dois vínculos do time — membros e lojas.
 *
 * Os dois `PUT` recebem o **conjunto inteiro** e são idempotentes, e é por isso que este
 * componente trabalha com a seleção completa em vez de emitir eventos de entrada e saída:
 * o que a tela tem em mãos é exatamente o corpo da requisição. Marcar o mesmo id duas vezes
 * não é erro — o contrato deduplica —, mas aqui nem acontece, porque a seleção é um
 * conjunto.
 *
 * Quem está selecionado sobe para o topo: numa agência com dezenas de lojas, conferir o que
 * já está no time é a pergunta mais frequente, e rolar a lista inteira atrás das marcadas é
 * o que o componente evita.
 */
export const TeamMultiSelect = ({
  options,
  selectedIds,
  onChange,
  loading = false,
  disabled = false,
  searchPlaceholder,
  emptyMessage,
}: TeamMultiSelectProps) => {
  const [busca, setBusca] = useState("");

  const selecionados = useMemo(() => new Set(selectedIds), [selectedIds]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtradas = termo
      ? options.filter(
          (opcao) =>
            opcao.label.toLowerCase().includes(termo) ||
            (opcao.hint ?? "").toLowerCase().includes(termo),
        )
      : options;

    return [...filtradas].sort((a, b) => {
      const marcadaA = selecionados.has(a.id) ? 0 : 1;
      const marcadaB = selecionados.has(b.id) ? 0 : 1;
      if (marcadaA !== marcadaB) return marcadaA - marcadaB;
      return a.label.localeCompare(b.label, "pt-BR");
    });
  }, [options, busca, selecionados]);

  const alternar = (id: string) => {
    if (disabled) return;
    const proximo = new Set(selecionados);
    if (proximo.has(id)) {
      proximo.delete(id);
    } else {
      proximo.add(id);
    }
    onChange([...proximo]);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          placeholder={searchPlaceholder}
          className="pl-9"
          disabled={disabled}
        />
      </div>

      {options.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        <ScrollArea className="h-64 rounded-lg border">
          <ul className="divide-y">
            {visiveis.map((opcao) => (
              <li key={opcao.id}>
                <label
                  className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 ${
                    disabled ? "cursor-not-allowed opacity-60" : ""
                  }`}
                >
                  <Checkbox
                    checked={selecionados.has(opcao.id)}
                    onCheckedChange={() => alternar(opcao.id)}
                    disabled={disabled}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm ${
                        opcao.muted ? "text-muted-foreground" : ""
                      }`}
                    >
                      {opcao.label}
                    </span>
                    {opcao.hint && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {opcao.hint}
                      </span>
                    )}
                  </span>
                  {opcao.badge && (
                    <Badge variant="outline" className="shrink-0 text-2xs">
                      {opcao.badge}
                    </Badge>
                  )}
                </label>
              </li>
            ))}
            {visiveis.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                Nada encontrado para “{busca}”.
              </li>
            )}
          </ul>
        </ScrollArea>
      )}

      <p className="text-xs text-muted-foreground">
        {selecionados.size === 0
          ? "Nenhum selecionado."
          : `${selecionados.size} selecionado${selecionados.size > 1 ? "s" : ""} de ${options.length}.`}
      </p>
    </div>
  );
};

export default TeamMultiSelect;
